<template>
	<v-window-item :value="relationType">
		<div
			v-if="loading"
			style="
				height: 300px;
				background-color: rgba(246, 247, 237, 0.5);
				border-radius: 15px;
			"
		>
			<LoadingState text="Loading data..."></LoadingState>
		</div>
		<div v-else>
			<div class="d-flex mt-4 justify-space-between align-center" v-if="!showForm">
				<v-text-field
					v-model="searchString"
					:label="searchLabel"
					class="custom-input flex-grow-1 me-2"
					bg-color="white"
					append-inner-icon="mdi-reload"
					@click:append-inner="reload"
				></v-text-field>
				<v-btn
					color="secondary"
					variant="tonal"
					icon="mdi-plus"
					aria-label="Add relationship"
					data-cy="add-relation-btn"
					@click="showForm = !showForm"
				></v-btn>
			</div>

			<div v-if="showForm" class="add-form-container mt-2 mb-2">
				<div class="add-form pa-4">
					<v-autocomplete
						:label="relationLabel"
						v-model="selectedItem"
						:items="availableItems"
						item-title="title"
						item-value="value"
						variant="outlined"
						class="custom-input mb-3"
						density="comfortable"
						color="primary"
						clearable
						:data-cy="`relation-${relationType}`"
					></v-autocomplete>
					<v-autocomplete
						v-if="askWhichChild"
						ref="childField"
						label="Which child is this teacher for?"
						v-model="selectedChild"
						:items="childOptions"
						item-title="title"
						item-value="value"
						variant="outlined"
						class="custom-input mb-3"
						density="comfortable"
						color="primary"
						data-cy="link-teacher-child"
						aria-required="true"
						:error="!!childError"
						:aria-describedby="childError ? 'link-teacher-child-error' : undefined"
						hide-details
						@update:model-value="childError = ''"
					></v-autocomplete>
					<p
						v-if="childError"
						id="link-teacher-child-error"
						role="alert"
						data-cy="link-teacher-child-error"
						class="child-choice-error"
					>
						{{ childError }}
					</p>
					<div class="d-flex justify-space-between pt-2">
						<v-btn
							color="primary"
							data-cy="add-relation-submit-btn"
							@click="addRelation"
							:loading="loading"
						>
							Add
						</v-btn>
						<v-btn
							color="gray400"
							variant="outlined"
							data-cy="cancel-relation-btn"
							@click="closeAddForm"
							:disabled="loading"
						>
							Cancel
						</v-btn>
					</div>
				</div>
			</div>

			<RelationshipTable
				:data="filteredData || []"
				@selection-change="onSelectionChange"
				@entity-click="handleEntityClick"
				:columns="columns"
			/>
			<div class="flex-fill mt-4">
				<v-btn color="primary" @click="removeRelationships" :loading="saving" block>
					Save Changes
				</v-btn>
			</div>
		</div>
	</v-window-item>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import type { NestedStudent } from "@/api_types/school";
import RelationshipTable from "@/components/school/directory/RelationshipTable.vue";
import LoadingState from "@/components/loadingState.vue";
import { complexSearch } from "@/utils";
import useDirectoryNavigation from "@/stores/school/directoryNavigation";
import { useAlertsStore } from "@/stores/alerts";
import useTeachers from "@/stores/school/teachers";
import useStudents from "@/stores/school/students";
import useParents from "@/stores/school/parents";
import useTeams from "@/stores/school/teams";
import useClasses from "@/stores/school/classes";

const teacherStore = useTeachers();
const studentStore = useStudents();
const parentStore = useParents();
const teamStore = useTeams();
const classStore = useClasses();
const directoryNav = useDirectoryNavigation();

const props = defineProps({
	entityType: String, // e.g., 'student'
	entityId: String, // e.g., '12345'
	relationType: String, // e.g., 'student'
	loading: Boolean,
	data: Array, // e.g., filteredData
	columns: Array, // Table column definitions
	searchLabel: String, // e.g., 'Search Students'
	relationLabel: String, // e.g., 'Student'
	reload: Function, // e.g., reloadData
	saving: Boolean,
	children: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits<{
	(e: 'entity-click', entityData: any): void;
}>();

const searchString = ref("");
const showForm = ref(false);
const selectedItem = ref(null);
const selectedChild = ref<string | null>(null);
const childError = ref("");
const childField = ref<{ focus?: () => void } | null>(null);
const unSelectedItems = ref([]);

const parentChildren = computed(() => {
	if (props.entityType !== "parent" || props.relationType !== "teacher") return [];
	return (props.children || []) as NestedStudent[];
});

const childLinkUid = (child: NestedStudent) => child.children_uid || child.uid || "";

const askWhichChild = computed(() => parentChildren.value.length > 1);

const childOptions = computed(() =>
	parentChildren.value
		.map((child) => ({
			title: `${child.child_fname || ""} ${child.child_lname || ""}`.trim() || "Child",
			value: childLinkUid(child),
		}))
		.filter((child) => child.value)
);

const childrenUidForLink = () => {
	if (props.entityType !== "parent" || props.relationType !== "teacher") return undefined;
	if (parentChildren.value.length === 1) {
		return childLinkUid(parentChildren.value[0]) || undefined;
	}
	if (askWhichChild.value) return selectedChild.value || undefined;
	return undefined;
};
function onSelectionChange(selection) {
	unSelectedItems.value = selection;
	console.log(unSelectedItems.value);
}
const filteredData = computed(() => {
	const list = props.data ?? [];
	if (!searchString.value) return list;

	return list.filter((item) => {
		const searchTarget = [
			item.title,
			item.child_fname,
			item.child_lname,
			item.firstname,
			item.lastname,
			item.team_name,
			item.teacher_fname,
			item.teacher_lname,
		]
			.filter(Boolean)
			.join(" "); // Combine all non-empty fields

		return complexSearch(searchString.value, searchTarget);
	});
});

const dataList = computed(() => props.data ?? []);

const availableItems = computed(() => {
	switch (props.relationType) {
		case "teacher":
			return (teacherStore.teachers?.data ?? [])
				.filter((i) => !i.deleted && !dataList.value.find((i2) => i2.uid === i.uid))
				.map((i) => ({
					title: `${i.teacher_fname} ${i.teacher_lname}`,
					value: i.uid,
				}));
		case "student":
			return (studentStore.students?.data ?? [])
				.filter((i) => !i.deleted && !dataList.value.find((i2) => i2.uid === i.uid))
				.map((i) => ({
					title: `${i.child_fname} ${i.child_lname}`,
					value: i.uid,
				}));
		case "parent":
			return (parentStore.parents?.data ?? [])
				.filter((i) => !i.deleted && !dataList.value.find((i2) => i2.uid === i.uid))
				.map((i) => ({
					title: `${i.firstname} ${i.lastname}`,
					value: i.uid,
				}));
		case "team":
			return (teamStore.teams?.data ?? [])
				.filter((i) => !i.deleted && !dataList.value.find((i2) => i2.uid === i.uid))
				.map((i) => ({
					title: `${i.team_name}`,
					value: i.uid,
				}));
		case "class": {
			const allClasses = classStore.data ?? [];
			const assignedClassIds = new Set(
				dataList.value
					.filter(
						(i2) =>
							i2 != null &&
							(i2.class_id !== undefined && i2.class_id !== null) &&
							allClasses.some((c) => String(c.class_id) === String(i2.class_id))
					)
					.map((i2) => String(i2.class_id))
			);
			return allClasses
				.filter((i) => !i.deleted && !assignedClassIds.has(String(i.class_id)))
				.map((i) => ({
					title: `${i.title ?? i.course_title ?? ""}`,
					value: i.class_id,
				}));
		}
		default:
			return [];
	}
});

async function removeRelationships() {
	const idsToDetach = (() => {
		switch (props.entityType) {
			case "teacher":
			case "student":
			case "parent":
			case "team":
				// When these entities have class relationships, use class_id, otherwise use uid
				return unSelectedItems.value.map((item) => 
					props.relationType === 'class' ? item.class_id : item.uid
				);
			case "class":
				// When class entity has teacher/student relationships, use their uid
				return unSelectedItems.value.map((item) => item.uid);
			default:
				return [];
		}
	})();

	console.log('removeRelationships debug:', {
		entityType: props.entityType,
		relationType: props.relationType,
		unSelectedItems: unSelectedItems.value,
		idsToDetach
	});

	switch (props.entityType) {
		case "teacher":
			await teacherStore.detach(props.entityId, props.relationType, idsToDetach);
			break;
		case "student":
			await studentStore.detach(props.entityId, props.relationType, idsToDetach);
			break;
		case "parent":
			await parentStore.detach(props.entityId, props.relationType, idsToDetach);
			break;
		case "team":
			await teamStore.detach(props.entityId, props.relationType, idsToDetach);
			break;
		case "class":
			await classStore.detach(props.entityId, props.relationType, idsToDetach);
			break;
		default:
			return null;
	}
	props.reload();
}

async function addRelation() {
	if (askWhichChild.value && !selectedChild.value) {
		childError.value = "Choose which child this teacher is for.";
		childField.value?.focus?.();
		return;
	}
	switch (props.entityType) {
		case "teacher":
			await teacherStore.attach(props.entityId, props.relationType, selectedItem.value);
			break;
		case "student":
			await studentStore.attach(props.entityId, props.relationType, selectedItem.value);
			break;
		case "parent": {
			const linkingTeacher = props.relationType === "teacher";
			const res = await parentStore.attach(
				props.entityId,
				props.relationType,
				selectedItem.value,
				childrenUidForLink(),
				linkingTeacher
			);
			if (linkingTeacher) {
				const failure = parentTeacherLinkFailure(res);
				if (failure.failed) {
					if (failure.alert) useAlertsStore().addAlert("error", failure.alert);
					return;
				}
			}
			break;
		}
		case "team":
			await teamStore.attach(props.entityId, props.relationType, selectedItem.value);
			break;
		case "class":
			await classStore.attach(props.entityId, props.relationType, selectedItem.value);
			break;
		default:
			return null;
	}
	closeAddForm();
	props.reload();
}

const FAILED_TEACHER_LINK = "Failed to link the teacher.";

function textMessage(message: unknown) {
	return typeof message === "string" && message.trim() ? message : undefined;
}

// FetchService already snackbars 403 and 423. 406 and other errors do not.
function parentTeacherLinkFailure(result: unknown): { failed: boolean; alert?: string } {
	if (result === false || result == null) {
		return { failed: true, alert: FAILED_TEACHER_LINK };
	}
	if (typeof result !== "object") {
		return { failed: true, alert: FAILED_TEACHER_LINK };
	}
	const body = result as {
		data?: { message?: unknown; status?: unknown };
		res?: { ok?: boolean; status?: number };
		status?: unknown;
		message?: unknown;
	};
	const httpStatus = body.res?.status;
	if (httpStatus === 403 || httpStatus === 423) return { failed: true };
	if (body.res && body.res.ok === false) {
		return { failed: true, alert: textMessage(body.data?.message) || FAILED_TEACHER_LINK };
	}
	if (body.res?.ok) {
		if (body.data?.status === false) {
			return { failed: true, alert: textMessage(body.data.message) || FAILED_TEACHER_LINK };
		}
		return { failed: false };
	}
	if (body.status === false || body.status === 500) {
		return { failed: true, alert: textMessage(body.message) || FAILED_TEACHER_LINK };
	}
	return { failed: false };
}

function closeAddForm() {
	showForm.value = false;
	selectedItem.value = null;
	selectedChild.value = null;
	childError.value = "";
}

// Handle entity clicks from the table
const handleEntityClick = (entity: any, field: string) => {
	// Determine the entity type based on the field clicked
	let targetEntityType = '';
	let entityId = '';
	let entityName = '';

	if (field === 'child_fname' || field === 'child_lname') {
		targetEntityType = 'student';
		entityId = entity.uid;
		entityName = `${entity.child_fname} ${entity.child_lname}`;
	} else if (field === 'teacher_fname' || field === 'teacher_lname') {
		targetEntityType = 'teacher';
		entityId = entity.uid;
		entityName = `${entity.teacher_fname} ${entity.teacher_lname}`;
	} else if (field === 'firstname' || field === 'lastname') {
		targetEntityType = 'parent';
		entityId = entity.uid;
		entityName = `${entity.firstname} ${entity.lastname}`;
	} else if (field === 'team_name') {
		targetEntityType = 'team';
		entityId = entity.uid;
		entityName = entity.team_name;
	} else if (field === 'title') {
		targetEntityType = 'class';
		entityId = entity.class_id;
		entityName = entity.title;
	}

	// Navigate to the entity using the store
	if (targetEntityType && entityId) {
		const entityData = {
			entityType: targetEntityType as any,
			entityId: entityId,
			entityName: entityName,
			entity: entity
		};
		
		// Emit to parent for modal closing
		emit('entity-click', entityData);
		
		// Navigate using the store
		directoryNav.navigateToEntity(entityData);
	}
};
</script>
<style scoped>
.child-choice-error {
	color: #7f1d1d;
	font-size: 0.875rem;
	margin: 0 0 12px;
}

.add-form-container {
	position: relative;

	.add-form {
		background-color: #f5f7fa;
		border-radius: 8px;
		border: 1px solid #e0e0e0;
		transition: all 0.2s ease;

		&:hover {
			border-color: #d0d0d0;
		}
	}
}
</style>
